import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fallbackBooks, fetchAcademyBooks } from '../academy/academyData';
import './BooksPage.css';

const CATEGORIES = [
  { key: 'all', label: '全部' },
  { key: '入门', label: '入门' },
  { key: '进阶', label: '进阶' },
];

// Cache books in memory for the session
let cachedBooks = null;

async function fetchBooks() {
  if (cachedBooks) return cachedBooks;
  cachedBooks = await fetchAcademyBooks();
  return cachedBooks;
}

function DifficultyBadge({ level }) {
  const cls = level === '入门' ? 'bk-diff beginner' : 'bk-diff advanced';
  return <span className={cls}>{level}</span>;
}

function BookCard({ book, onClick }) {
  return (
    <article className="bk-card" onClick={() => onClick(book.id)}>
      <div className="bk-cover-wrap">
        <img className="bk-cover" src={book.cover} alt={book.title} loading="lazy" />
      </div>
      <div className="bk-body">
        <DifficultyBadge level={book.difficulty} />
        <h3 className="bk-title">{book.title}</h3>
        <p className="bk-author">{book.author}</p>
        <p className="bk-summary">{book.summary}</p>
      </div>
      <svg className="bk-arrow" width="16" height="16" fill="none" viewBox="0 0 24 24">
        <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </article>
  );
}

export default function BooksPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [books, setBooks] = useState(fallbackBooks);

  useEffect(() => {
    fetchBooks().then(setBooks);
  }, []);

  const filteredBooks = useMemo(() => {
    let result = books;
    if (activeCategory !== 'all') {
      result = result.filter((b) => b.difficulty === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (b) =>
          b.title.toLowerCase().includes(q) ||
          b.author.toLowerCase().includes(q) ||
          b.summary.toLowerCase().includes(q)
      );
    }
    return result;
  }, [activeCategory, searchQuery, books]);

  const openBook = (bookId) => {
    navigate(`/book/${bookId}`);
  };

  return (
    <main className="bk-page">
      <header className="bk-hero">
        <h1>书籍库</h1>
        <p>精选投资经典，提炼核心精华，帮你用最短时间掌握最关键的知识</p>
      </header>

      <div className="bk-layout">
        {/* 左侧分类栏 */}
        <aside className="bk-sidebar">
          <div className="bk-sidebar-label">分类</div>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              className={`bk-sidebar-btn${activeCategory === cat.key ? ' active' : ''}`}
              onClick={() => setActiveCategory(cat.key)}
            >
              {cat.label}
              <span className="bk-sidebar-count">
                {cat.key === 'all'
                  ? books.length
                  : books.filter((b) => b.difficulty === cat.key).length}
              </span>
            </button>
          ))}
        </aside>

        {/* 右侧内容区 */}
        <div className="bk-main">
          <div className="bk-search-wrap">
            <svg className="bk-search-icon" width="16" height="16" fill="none" viewBox="0 0 24 24">
              <path d="M21 21l-4.35-4.35M11 19a8 8 0 110-16 8 8 0 010 16z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <input
              type="text"
              className="bk-search-input"
              placeholder="搜索书名、作者..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {filteredBooks.length === 0 ? (
            <div className="bk-empty">没有找到匹配的书籍</div>
          ) : (
            <div className="bk-grid">
              {filteredBooks.map((book) => (
                <BookCard key={book.id} book={book} onClick={openBook} />
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
